import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthentication, useNotifications, useSettings, useWorkstation } from '@connect-x-app/hybrid-state';
import { Text, Toggle } from '@connect-x-app/hybrid-ui';
import { FlightStatus, TimeZone, WorkstationState } from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { isWorkstationEmpty } from '@connect-x-app/shared-utils';
import { useApp } from '@connect-x-app/web-state';
import { FLIGHTROOM_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Divider, Icon, SearchField } from '@connect-x-app/web-ui';
import { useDate } from '@connect-x-app/web-utils';

import { TabBar } from '../tabBar/TabBar';
import { TabBar as TabBarType } from '../tabBar/TabBar.types';
import { WorkstationCollapse } from '../WorkstationCollapse/WorkstationCollapse';

import './FlightroomsHeaderComponent.scss';

type Props = {
  handleWorkstationClose: () => void;
  searchQuery: string;
  selectedFlightroomTab?: FlightStatus;
  setSearchQuery: (query: string) => void;
  setSelectedFlightroomTab: (status: FlightStatus) => void;
  workstation: WorkstationState | null;
};

export const FlightRoomsHeaderComponent = ({
  handleWorkstationClose,
  searchQuery,
  selectedFlightroomTab,
  setSearchQuery,
  setSelectedFlightroomTab,
  workstation,
}: Props) => {
  const { t } = useTranslation();
  const { canSearchFlightRooms, canViewFlightRooms, canViewTasks } = useAuthentication();
  const { isNotificationCenterVisible, isProfileVisible, setIsNotificationCenterVisible, setIsProfileVisible } = useApp();
  const { unreadNotificationsCount } = useNotifications();
  const { handleWorkstationOpen, isWorkstationOpen } = useWorkstation();
  const { settings, updateTimeZoneSetting } = useSettings();
  const { time } = useDate(settings.preferences.timeZone);

  const tabs: TabBarType[] = useMemo(() => {
    let initialTabs = [
      {
        id: FlightStatus.ACTIVE,
        onClick: () => setSelectedFlightroomTab(FlightStatus.ACTIVE),
        text: t('FLIGHTROOMS.FLIGHTROOM_TAB_TITLE.ACTIVE'),
      },
      {
        id: FlightStatus.LANDED,
        onClick: () => setSelectedFlightroomTab(FlightStatus.LANDED),
        text: t('FLIGHTROOMS.FLIGHTROOM_TAB_TITLE.LANDED'),
      },
    ] as TabBarType[];
    if (canViewTasks) {
      initialTabs = [
        ...initialTabs,
        {
          id: FlightStatus.TASKS,
          onClick: () => setSelectedFlightroomTab(FlightStatus.TASKS),
          text: t('FLIGHTROOMS.FLIGHTROOM_TAB_TITLE.TASKS'),
        },
      ];
    }

    return initialTabs;
  }, [canViewTasks, setSelectedFlightroomTab, t]);

  const isSearchEnabled = canViewFlightRooms && canSearchFlightRooms;

  const getSelectedTab = () => {
    return tabs.find(({ id }) => selectedFlightroomTab === id)?.id;
  };

  return (
    <header className="flightrooms-header-container">
      <div className="flightrooms-header-top-container">
        <div className="flightrooms-header-top-left">
          <Text size="SM" weight={'bold'}>
            {time}
          </Text>
          <Toggle
            onChange={(value: string) => updateTimeZoneSetting(value as TimeZone)}
            testId={FLIGHTROOM_TEST_IDS.header.timeZone}
            toggleValue={settings.preferences.timeZone}
            valueOptions={[TimeZone.UTC, TimeZone.LT]}
          />
        </div>
        <div className="flightrooms-header-top-right">
          <Icon
            badgeText={unreadNotificationsCount}
            colorName={isNotificationCenterVisible ? COLORS.romeo.$07 : COLORS.zulu.$08}
            disabled={!canViewFlightRooms}
            hoverColorName={isNotificationCenterVisible ? COLORS.zulu.$08 : COLORS.romeo.$07}
            name={isNotificationCenterVisible ? 'notificationBellFill' : 'notificationBellOutline'}
            onClick={() => setIsNotificationCenterVisible(!isNotificationCenterVisible)}
            showBadge={canViewFlightRooms && unreadNotificationsCount > 0}
            size={2.4}
            testId={FLIGHTROOM_TEST_IDS.header.notificationCenter}
          />
          <Icon
            colorName={isProfileVisible ? COLORS.romeo.$07 : COLORS.zulu.$08}
            hoverColorName={isProfileVisible ? COLORS.zulu.$08 : COLORS.romeo.$07}
            name={isProfileVisible ? 'profileFill' : 'profileOutline'}
            onClick={() => setIsProfileVisible(!isProfileVisible)}
            size={2.4}
            testId={FLIGHTROOM_TEST_IDS.header.profile}
          />
          <Divider color={COLORS.zulu.$04} height={2.4} />
          <Icon
            colorName={isWorkstationOpen ? COLORS.romeo.$07 : COLORS.zulu.$08}
            hoverColorName={isWorkstationOpen ? COLORS.zulu.$08 : COLORS.romeo.$07}
            name={isWorkstationOpen ? 'deviceMonitorAircraftTemplateFill' : 'deviceMonitorAircraftTemplateOutline'}
            onClick={() => (isWorkstationOpen ? handleWorkstationClose() : handleWorkstationOpen())}
            showBadge={!isWorkstationEmpty(workstation)}
            size={2.4}
            testId={FLIGHTROOM_TEST_IDS.header.workstation}
          />
        </div>
      </div>
      {!searchQuery ? <WorkstationCollapse handleWorkstationClick={handleWorkstationOpen} workstation={workstation} /> : null}
      <div className="flightrooms-header-search">
        <SearchField
          disabled={!isSearchEnabled}
          search={searchQuery}
          setSearch={setSearchQuery}
          testId={FLIGHTROOM_TEST_IDS.header.search}
        />
      </div>
      {!searchQuery ? (
        <div className="flightrooms-header-nav-container">
          <TabBar selected={getSelectedTab()} tabs={tabs} testId={FLIGHTROOM_TEST_IDS.list.tabs} />
        </div>
      ) : null}
    </header>
  );
};
